// frontend/src/pages/Notifications.js
/**
 * Notifications Page - Mobile-First Design
 * 
 * Features:
 * - Mobile-first filtering (tabs + category pills)
 * - Load More pagination (no page numbers)
 * - Always-visible action buttons
 * - Conditional grouping (New/Earlier only on "All" tab)
 * - Skeleton loading states
 * - Empty states
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCircle2,
  AlertTriangle,
  Info,
  FileText,
  Calendar,
  Clock,
  Wallet,
  UserCheck,
  Megaphone,
  Inbox,
  Filter,
  X,
  Settings,
  Moon,
  CalendarClock,
  Package,
  Cake
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { useTabPersistence } from '../hooks/usePersistence';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from '../utils/toast';
import { safeNavigateToNotificationUrl } from '../utils/urlUtils';
import { CountBadge } from '../components/UI/CountBadge';

const VALID_TABS = ['all', 'unread', 'requires-action'];

const CATEGORIES = [
  { value: null, label: 'All', icon: Bell },
  { value: 'leave', label: 'Leave', icon: Calendar },
  { value: 'attendance', label: 'Attendance', icon: Clock },
  { value: 'documents', label: 'Documents', icon: FileText },
  { value: 'payroll', label: 'Payroll', icon: Wallet },
  { value: 'compliance', label: 'Compliance', icon: AlertTriangle },
  { value: 'employment', label: 'Employment', icon: UserCheck },
  { value: 'nightwork', label: 'Nightwork', icon: Moon },
  { value: 'restday_work', label: 'Rest Day Work', icon: CalendarClock },
  { value: 'assets', label: 'Assets', icon: Package },
  { value: 'celebrations', label: 'Celebrations', icon: Cake },
  { value: 'system', label: 'System', icon: Megaphone },
];

const Notifications = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useTabPersistence('notifications', 'all', VALID_TABS);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [offset, setOffset] = useState(0);
  const [allNotifications, setAllNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 20;

  // Build filter params based on active tab and category
  const filterParams = useMemo(() => {
    const params = {
      limit: pageSize,
      offset: offset,
    };

    // Apply tab filters
    if (activeTab === 'unread') {
      params.is_read = false;
    } else if (activeTab === 'requires-action') {
      params.requires_action = true;
    }

    // Apply category filter
    if (categoryFilter) {
      params.category = categoryFilter;
    }

    return params;
  }, [activeTab, categoryFilter, offset]);

  // Track current filter identity (without offset) to match data with filters
  const currentFilterKey = useMemo(() => 
    JSON.stringify({ activeTab, categoryFilter }), 
    [activeTab, categoryFilter]
  );
  const expectedFilterKeyRef = useRef(currentFilterKey);
  const MAX_NOTIFICATIONS = 200; // Cap accumulated notifications to prevent memory leaks

  // Fetch notifications (disable toasts since user is already viewing notifications page)
  const {
    notifications: fetchedNotifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    isMarkingAsRead,
    isMarkingAllAsRead,
    paginationNext,
  } = useNotifications({ params: filterParams, enablePolling: false, enableToasts: false });

  // Update expected filter key when filters change and reset offset
  useEffect(() => {
    expectedFilterKeyRef.current = currentFilterKey;
    setOffset(0);
    // Clear notifications immediately when filter changes to show loading state
    setAllNotifications([]);
    setHasMore(true);
  }, [currentFilterKey]);

  // Update accumulated notifications and hasMore flag
  // Only update if data matches current filter (prevents race conditions from stale queries)
  useEffect(() => {
    // Don't update if still loading or no data
    if (loading || !fetchedNotifications || !Array.isArray(fetchedNotifications)) {
      return;
    }

    // Only update if this data matches the current expected filter
    // This prevents stale query results from overwriting current data
    if (expectedFilterKeyRef.current === currentFilterKey) {
      if (offset === 0) {
        // First page or filter change - replace all notifications
        setAllNotifications(fetchedNotifications);
      } else {
        // Loading more - append to existing, but cap at MAX_NOTIFICATIONS
        setAllNotifications(prev => {
          const combined = [...prev, ...fetchedNotifications];
          // If we exceed the limit, keep only the most recent items
          if (combined.length > MAX_NOTIFICATIONS) {
            return combined.slice(-MAX_NOTIFICATIONS);
          }
          return combined;
        });
      }
      
      // Fix pagination: Use backend's 'next' field if available, otherwise check count
      // If backend returns 'next' as null, there are no more pages
      // If 'next' is undefined (not using DRF pagination), fall back to count check
      if (paginationNext !== undefined) {
        // DRF pagination: 'next' is null when no more pages
        setHasMore(paginationNext !== null);
      } else {
        // Fallback: check if returned count equals pageSize (might have more)
        setHasMore(fetchedNotifications.length === pageSize);
      }
    }
  }, [fetchedNotifications, offset, pageSize, currentFilterKey, loading, paginationNext]);

  // Get notification icon with colored background
  const getNotificationIcon = (iconType, category) => {
    const iconConfigs = {
      success: {
        bg: 'bg-emerald-50',
        iconColor: 'text-emerald-500',
        Icon: CheckCircle2
      },
      warning: {
        bg: 'bg-amber-50',
        iconColor: 'text-amber-500',
        Icon: AlertTriangle
      },
      error: {
        bg: 'bg-red-50',
        iconColor: 'text-red-500',
        Icon: AlertTriangle
      },
      info: {
        bg: 'bg-blue-50',
        iconColor: 'text-blue-500',
        Icon: Info
      }
    };

    const categoryIcons = {
      leave: Calendar,
      attendance: Clock,
      documents: FileText,
      payroll: Wallet,
      compliance: AlertTriangle,
      employment: UserCheck,
      nightwork: Moon,
      restday_work: CalendarClock,
      assets: Package,
      celebrations: Cake,
      system: Megaphone,
    };

    const config = iconConfigs[iconType] || iconConfigs.info;
    const CategoryIcon = categoryIcons[category] || config.Icon;

    return (
      <div className={`w-9 h-9 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <CategoryIcon className={`w-4 h-4 ${config.iconColor}`} />
      </div>
    );
  };

  // Group notifications (only for "All" tab)
  // Optimized: Only compute grouping when needed and cache results
  const groupedNotifications = useMemo(() => {
    if (activeTab !== 'all') {
      return { newItems: allNotifications, earlierItems: [] };
    }
    
    // Optimize: Use single pass to separate read/unread instead of two filters
    const newItems = [];
    const earlierItems = [];
    
    for (const notification of allNotifications) {
      if (notification.is_read) {
        earlierItems.push(notification);
      } else {
        newItems.push(notification);
      }
    }
    
    return { newItems, earlierItems };
  }, [allNotifications, activeTab]);

  // Handle notification click
  const handleNotificationClick = (notification, e) => {
    if (e.target.closest('[data-action]')) return;

    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    if (notification.action_url) {
      // Validate and safely navigate to action URL
      const navigated = safeNavigateToNotificationUrl(notification.action_url, navigate);
      if (!navigated) {
        toast.error('Invalid notification link');
      }
    }
  };

  // Handle mark as read
  const handleMarkAsRead = (e, notificationId) => {
    e.stopPropagation();
    markAsRead(notificationId);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsRead();
    toast.success('All notifications marked as read');
  };

  // Handle load more
  const handleLoadMore = () => {
    setOffset(prev => prev + pageSize);
  };

  // Handle category filter change
  const handleCategoryFilter = (category) => {
    setCategoryFilter(category === null ? null : category);
  };

  // Notification Card Component
  const NotificationCard = ({ notification }) => {
    return (
      <div
        className={`
          group relative px-4 py-3 cursor-pointer transition-all duration-150 border-b border-gray-200 table-row
          ${!notification.is_read 
            ? 'bg-blue-50/50' 
            : 'bg-white'
          }
        `}
        onClick={(e) => handleNotificationClick(notification, e)}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          {getNotificationIcon(notification.icon_type, notification.category)}
          
          {/* Content */}
          <div className="flex-1 min-w-0 pr-10">
            {/* Title with unread dot */}
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium leading-tight truncate ${
                !notification.is_read ? 'text-gray-900' : 'text-gray-600'
              }`}>
                {notification.title}
              </p>
              {!notification.is_read && (
                <span className="w-2 h-2 bg-blue-800 rounded-full flex-shrink-0" />
              )}
            </div>
            
            {/* Message */}
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
              {notification.message}
            </p>
            
            {/* Timestamp */}
            <p className="text-[11px] text-gray-400 mt-1.5">
              {notification.time_ago}
            </p>
          </div>

          {/* Always-visible Actions (Mobile-First) */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            {!notification.is_read && (
              <button
                data-action="mark-read"
                onClick={(e) => handleMarkAsRead(e, notification.id)}
                disabled={isMarkingAsRead}
                className="icon-button disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mark as read"
                aria-label="Mark as read"
              >
                {isMarkingAsRead ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Check className="w-4 h-4 text-gray-500" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Section Header
  const SectionHeader = ({ title, count }) => (
    <div className="px-4 py-2.5 bg-gray-50/80 border-b border-gray-100 sticky top-0 z-10">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </p>
        {count > 0 && (
          <CountBadge count={count} theme="employee" size="md" />
        )}
      </div>
    </div>
  );

  // Empty State
  const EmptyState = ({ message, submessage }) => (
    <div className="py-12 px-6 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
        <Inbox className="w-8 h-8 text-gray-400" />
      </div>
      <h4 className="text-lg font-semibold text-gray-900 mb-1">
        {message}
      </h4>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">
        {submessage}
      </p>
    </div>
  );

  // Loading Skeleton
  if (loading && allNotifications.length === 0) {
    return (
      <div className="space-y-6 bg-gray-50 min-h-screen">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
              </div>
            </div>
            <div className="h-8 bg-gray-200 rounded-full w-24 animate-pulse"></div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-full w-28 animate-pulse flex-shrink-0"></div>
          ))}
        </div>

        {/* Category Pills Skeleton */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-full w-24 animate-pulse flex-shrink-0"></div>
          ))}
        </div>

        {/* Notification Cards Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="px-4 py-4 border-b border-gray-100 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading notifications</h3>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine empty state message
  const getEmptyStateMessage = () => {
    if (activeTab === 'unread') {
      return {
        message: "You're all caught up!",
        submessage: "No unread notifications. Check back later for updates."
      };
    }
    if (activeTab === 'requires-action') {
      return {
        message: "No action required",
        submessage: "All notifications are resolved. You're all set!"
      };
    }
    return {
      message: "No notifications yet",
      submessage: "You'll see updates and important information here when available."
    };
  };

  const emptyState = getEmptyStateMessage();

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-blue-800" />
            </div>
            <div>
              <h1 className="text-subsection-header inline-flex items-center gap-2">
                Notifications
                {unreadCount > 0 && (
                  <CountBadge count={unreadCount} theme="employee" size="md" />
                )}
              </h1>
              <p className="text-body mt-1">View and manage your notifications</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {allNotifications.length > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllAsRead}
                className="icon-button disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mark all as read"
                aria-label="Mark all as read"
              >
                {isMarkingAllAsRead ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/notifications/preferences')}
              className="icon-button"
              title="Notification Preferences"
              aria-label="Notification Preferences"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile scroll, desktop wrap */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {[
          { id: 'all', label: 'All', icon: Bell },
          { id: 'unread', label: 'Unread', icon: AlertTriangle },
          { id: 'requires-action', label: 'Requires Action', icon: CheckCircle2 },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn ${isActive ? 'active' : ''}`}
              title={tab.label}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Category Pills - Mobile scroll, desktop wrap */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4">
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isActive = categoryFilter === category.value;
          return (
            <button
              key={category.value || 'all'}
              onClick={() => handleCategoryFilter(category.value)}
              className={`tab-btn tab-btn-sm ${isActive ? 'active' : ''}`}
              title={category.label}
            >
              <Icon className="w-4 h-4" />
              {category.label}
            </button>
          );
        })}
      </div>

      {/* Notification List */}
      {allNotifications.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border">
          <EmptyState {...emptyState} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {/* Conditional Grouping - Only show "New/Earlier" on "All" tab */}
          {activeTab === 'all' ? (
            <>
              {/* New (Unread) Section */}
              {groupedNotifications.newItems.length > 0 && (
                <>
                  <SectionHeader title="New" count={groupedNotifications.newItems.length} />
                  <div className="divide-y divide-gray-200">
                    {groupedNotifications.newItems.map((notification) => (
                      <NotificationCard key={notification.id} notification={notification} />
                    ))}
                  </div>
                </>
              )}

              {/* Earlier (Read) Section */}
              {groupedNotifications.earlierItems.length > 0 && (
                <>
                  <SectionHeader title="Earlier" count={groupedNotifications.earlierItems.length} />
                  <div className="divide-y divide-gray-200">
                    {groupedNotifications.earlierItems.map((notification) => (
                      <NotificationCard key={notification.id} notification={notification} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            // No grouping for other tabs
            <div className="divide-y divide-gray-200">
              {allNotifications.map((notification) => (
                <NotificationCard key={notification.id} notification={notification} />
              ))}
            </div>
          )}

          {/* Load More Button - Mobile-First */}
          {hasMore && (
            <div className="px-4 py-6 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="w-full md:w-auto md:mx-auto md:max-w-md min-h-[44px] btn btn-outline flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More
                    {allNotifications.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({allNotifications.length} shown)
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;

